import { functions, firestore, firebaseAuth } from '../../../shared/config/firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { create } from 'zustand';

export interface GenerateOcdCycleStepsResponse {
  ocdCycleSteps: OcdCycleStep[];
}

export interface GenerateOcdCycleStepsRequest {}

interface OnboardingOcdCycleState {
  isZoomedIn: boolean;
  ocdCycleSteps: OcdCycleStep[];
  currentOcdCycleStep: OcdCycleStep;
  setStep: (step: number) => void;
  generateOcdCycleSteps: () => Promise<void>;
  loadOcdCycleSteps: () => Promise<void>;
}

export interface OcdCycleStep {
  ollieText: string;
  cycleStep: number;
  buttonLabel: string;
}

const buttonLabels = [
  'True...',
  'Exactly!',
  'Mhm',
  'Yeah...',
  'Okay',
  'That makes sense',
  'I see',
  'Oh...',
  'But how?',
  'Sounds good!',
  'Create my plan!',
];

const initialState = {
  isZoomedIn: false,
  ocdCycleSteps: [],
  currentOcdCycleStep: {
    ollieText: '',
    cycleStep: -1,
    buttonLabel: '',
  },
};

export const useOnboardingOcdCycleStore = create<OnboardingOcdCycleState>((set, get) => ({
  ...initialState,
  setStep: (step: number) => {
    const currentState = get();
    const ocdCycleSteps = currentState.ocdCycleSteps;

    if (step < 0 || ocdCycleSteps.length <= 0) {
      return;
    }

    if (step >= ocdCycleSteps.length) {
      set({ isZoomedIn: true });
      return;
    }

    set({ currentOcdCycleStep: ocdCycleSteps[step], isZoomedIn: false });
  },
  generateOcdCycleSteps: async () => {
    const generateOcdCycleStepsCallable = httpsCallable<GenerateOcdCycleStepsRequest, GenerateOcdCycleStepsResponse>(
      functions,
      'generateOcdCycleSteps'
    );
    const result = await generateOcdCycleStepsCallable();
    const response = result.data;

    const ocdCycleSteps = response.ocdCycleSteps.map((step, index) => ({
      ...step,
      buttonLabel: buttonLabels[index],
    }));

    set({ ocdCycleSteps, currentOcdCycleStep: ocdCycleSteps[0] });
  },
  loadOcdCycleSteps: async () => {
    // If already loaded in memory, return early
    const currentSteps = get().ocdCycleSteps;
    if (currentSteps.length > 0) {
      return;
    }

    try {
      const userId = firebaseAuth.currentUser?.uid;
      if (!userId) {
        console.error('User not authenticated');
        return;
      }

      const docRef = doc(firestore, `user_info/${userId}/metadata/ocd_cycle_steps`);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const steps = data.steps as { ollieText: string; cycleStep: number }[];

        const ocdCycleSteps = steps.map((step, index) => ({
          ...step,
          buttonLabel: buttonLabels[index],
        }));

        set({ ocdCycleSteps, currentOcdCycleStep: ocdCycleSteps[0] });
      }
    } catch (error) {
      console.error('Error loading OCD cycle steps from database:', error);
    }
  },
}));
